/**
 * Operator-facing copy. Paint sentences, never snake_case / UUIDs / hex ids.
 * Wire codes stay in this map (and in data-* attrs) so paint tests can grep them.
 */

const SAY = Object.freeze({
  l5s_conc: "This result is still open. Someone needs to close it.",
  clo_l5s: "A check is still open",
  objective_blocked: "This step cannot finish until you decide",
  permission_conflict: "This seat cannot do that yet",
  resource_conflict: "Another job is using this board",
  proof_timeout: "Proof did not arrive in time",
  needs_you: "Needs you",
  imminent_timeout: "This is about to time out",
  durable_store_unavailable: "The record store is not ready",
  exception: "Needs a look",
  wait_nearby_live: "Waiting for nearby live to play",
  wait_live_loop: "Waiting for each live camera to close its proof",
  live_loops_open: "Waiting for each live camera to close its proof",
  wait_record_proof: "Waiting for a recording attach",
  wait_record_skill: "Waiting for a recording attach",
  two_lens_incomplete: "Need a person camera and a board camera before this can close",
  stale_planning_epoch: "This order is from an old judgement. The Watch will not run it.",
  no_change: "Same facts · the Watch did not change its mind",
  epoch_budget: "The Watch has already thought as many times as it is allowed tonight",
  legacy_bind_current: "This order did not name a judgement. It binds to the current one.",
  epoch_not_open: "The Watch has not opened a judgement yet",
  epoch_bound: "Bound to this Watch judgement",
  human_channel: "Human channel — not a Team exit",
  team_exit: "Team exit",
  duty_bond_unbound: "Tonight's named watch is not bound yet",
  dispatch_still_planned: "Dispatch is still on the plan",
  no_fresh_responder: "No one is on site yet",
  fresh_person_requires_human: "A person on site still needs a human call",
  no_alarm_location: "We do not have a place for this alarm",
  origin_unbound: "The camera at the point is not bound yet",
  camera_digest_required: "This camera still needs a digest",
  camera_id_required: "This camera is not named yet",
  dispatch_resource_site_unknown: "Dispatch does not know this site",
  live_quota_user: "This seat's live camera limit is full",
  live_quota_device: "This board's live camera limit is full",
  live_quota_console: "This console's live camera limit is full",
  live_quota_tenant: "This site's live camera limit is full",
  live_quota_global: "The platform live camera limit is full",
  live_quota_i1_starved: "Live cameras are reserved for a higher alarm",
  live_quota_alarm_reserve: "Live cameras are held for this alarm",
  running_ai: "Team is running",
  held: "Held",
  human_command: "You have the wheel",
  blocked: "Blocked",
  degraded_rules_only: "Rules only — the model is not in charge",
  alarm: "Alarm",
  panic: "Panic",
  intrusion: "Intrusion",
  tamper: "Tamper",
  fire: "Fire",
  camera_live: "Live camera",
  camera_snapshot: "Snapshot",
  camera_record: "Recording",
  incident_close: "Close the case",
  dispatch_person: "Send a person",
  siren_on: "Siren",
  notify: "Notify",
  AUTO: "Automatic",
  MONITOR: "Watch",
  PAUSED: "Paused",
  MANUAL_ONLY: "Manual only",
  observe: "Watching",
  off: "Off",
  in_process: "In process",
  pg: "Live store",
  ready: "Ready",
  verified: "Verified",
  unverified: "Not verified yet",
  committed: "Done",
  denied: "Refused",
  queued: "Queued",
  en_route: "On the way",
  enroute: "On the way",
  on_scene: "On scene",
  onscene: "On scene",
  accepted: "Accepted",
  assigned: "Assigned",
  active: "Active",
  cancelled: "Cancelled",
  resolved: "Closed",
  pending: "Pending",
  "not ready": "Not ready",
  event: "Event",
  open: "Open",
  closed: "Closed",
  draft: "Draft",
  status_written: "Status saved",
  unobserved: "Not seen yet",
  admitted: "Admitted",
  executed: "Done",
  playing: "Playing",
  snapshot: "Snapshot",
  receipt_not_verified: "Receipt is not verified",
  inspect_unavailable: "Still confirming this event",
  no_plan: "No plan yet",
  families_mismatch: "Rules on this site do not match the wanted set",
  lag: "Waiting",
  ACK: "Answered",
  "rules shadow; policy can veto": "Rules shadow; policy can veto",
  "Team running": "Team is running",
  "writes frozen": "Writes are frozen",
  "execution blocked": "Action is blocked",
  "no proof yet": "No proof yet",
  "no AI owner yet": "No AI owner yet",
  "human takeover": "You have the wheel",
  "model or path degraded": "Model or path is degraded",
  independence_down: "Independence is down",
  coordinator: "The team planned this",
  enrich: "Site facts",
  replica: "Live copy",
  listed_not_walked: "On the list — not walked yet",
  listed: "On the list",
  pass: "Passed",
  fail: "Failed",
  skip: "Skipped",
  live: "Live",
  authoritative_binding_and_media_played: "Live picture is on",
  awaiting_media_played: "Waiting for the live picture",
  live_session_missing_or_binding_mismatch: "This camera is not live yet",
  binding_disabled: "This camera is turned off",
  active_session_without_media_ready_evidence: "Live is open · no picture yet",
  registry_budget_hold: "Cameras are busy. Try Refresh",
  "no live walk": "Not walked live yet",
  ghost: "Rehearsal",
  rehearsal: "Rehearsal",
  contested: "Contested",
  evidence_gap: "Waiting for evidence",
  clear: "Clear",
  rails_only: "Rules only",
  never_dialed: "Not started",
  empty_dial: "No dial yet",
  dial_fail: "Dial failed",
  judge: "Judge",
  JUDGE: "Judge",
  veto: "Policy",
  VETO: "Policy",
  act: "Action",
  ACT: "Action",
  prove: "Proof",
  PROVE: "Proof",
  STOP: "Stop",
  stop: "Stop",
  engaged: "Engaged",
  half: "Half",
  soft_preview: "Preview",
  idle: "Quiet",
  EVENT: "Event",
});

export const KAMPUNG_HONESTY = "Named consented household · not mass AUTO";

export function kampungTonightSay(raw) {
  const s = String(raw || "").trim();
  if (!s || /mass AUTO|consented household/i.test(s) || looksLikeCode(s)) {
    return "Only a named household that agreed";
  }
  return operatorSay(s, s);
}

function mapped(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  if (SAY[t]) return SAY[t];
  const lower = t.toLowerCase();
  if (SAY[lower]) return SAY[lower];
  const upper = t.toUpperCase();
  if (SAY[upper]) return SAY[upper];
  return "";
}

export function looksLikeCode(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return false;
  if (/[\uFFFD\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(s)) return true;
  if (/^(ai-inc-|trun-|uds-|CROC-|qa-fx-|qa-|device:|cam:|camera:)/i.test(s)) return true;
  if (/\bCROC-[0-9A-F]{8,}\b/i.test(s)) return true;
  if (/\bqa-fx-/i.test(s)) return true;
  if (/\bcam-[0-9a-f]{8,}\b/i.test(s)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return true;
  if (/^[0-9a-f]{16,}$/i.test(s)) return true;
  if (/^(cam|camid|camera)[_-]/i.test(s)) return true;
  if (/^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)+$/i.test(s)) return true;
  if (/^[a-z][a-z0-9]*(_[a-z0-9]+)+$/i.test(s)) return true;
  if (/^[a-z]{1,8}\d+[a-z0-9_]*$/i.test(s) && /_/.test(s)) return true;
  if (/[{}[\]<>\\]/.test(s)) return true;
  if (/^[a-z0-9]+(?:-[a-z0-9]+){3,}$/i.test(s)) return true;
  if (/^[A-Z]{3,}-[0-9A-F]{8,}$/i.test(s)) return true;
  if (/\/(ai|api)\//i.test(s)) return true;
  if (/#\/[a-z0-9]/i.test(s)) return true;
  if (/https?:\/\//i.test(s)) return true;
  if (/\b(?:GET|POST|PUT|PATCH|DELETE)\s+\//i.test(s)) return true;
  if (/\blocalhost\b/i.test(s)) return true;
  if (/:(?:80|443|8080|8088|8443|9231|18999)\b/.test(s)) return true;
  return false;
}

export function operatorSay(raw, fallback) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s || s === "—") return fallback || "—";
  const hit = mapped(s);
  if (hit) return hit;
  const modePair = s.match(/^(AUTO|MONITOR|PAUSED|MANUAL_ONLY)\/(AUTO|MONITOR|PAUSED|MANUAL_ONLY)$/i);
  if (modePair) {
    return (mapped(modePair[1]) || modePair[1]) + " / " + (mapped(modePair[2]) || modePair[2]);
  }
  const head = s.split(/[·|,]/)[0].trim();
  const headHit = mapped(head);
  if (headHit) return headHit;
  const colon = s.indexOf(":");
  if (colon > 0) {
    const leftRaw = s.slice(0, colon).trim();
    if (/^tombstone$/i.test(leftRaw)) {
      return operatorSay(s.slice(colon + 1), "Held — do not start a second run");
    }
    const left = mapped(leftRaw);
    const right = operatorSay(s.slice(colon + 1), "");
    if (left && right && right !== "—") return left + " · " + right;
    if (left) return left;
  }
  if (looksLikeCode(s)) return fallback || "Needs a look";
  if (/^[a-z]{4,16}$/.test(s)) return s.charAt(0).toUpperCase() + s.slice(1);
  return s.slice(0, 140);
}

export function whySay(raw) {
  return operatorSay(raw, "Nothing is blocking this");
}

export function boardHeadingOf(id) {
  const s = String(id || "").trim();
  if (!s) return "No board named";
  if (looksLikeCode(s)) return "A board on this site";
  return s.slice(0, 48);
}

/** Painted board name. Never fall back to CROC-/SN-/qa- wire ids. */
export function boardLabelSay(rowOrLabel, id) {
  const r = rowOrLabel && typeof rowOrLabel === "object" ? rowOrLabel : null;
  const human = r
    ? String(r.display_label || r.map_label || r.label || r.name || "").trim()
    : String(rowOrLabel || "").trim();
  const did = String(id || (r && (r.device_id || r.id)) || "").trim();
  if (human && !looksLikeCode(human)) return human.slice(0, 64);
  return boardHeadingOf(did);
}

/* Paint only. Wire SKU stays in data-kind / title. Honesty: Waveshare ESP32-P4-WIFI6-POE-ETH */
const BOARD_KIND_SAY = Object.freeze({
  waveshare_esp32p4_poe_eth_v1: "Sentinel board",
  "waveshare-esp32-p4-wifi6-poe-eth": "Sentinel board",
  esp32p4_poe_eth: "Sentinel board",
  waveshare_esp32_p4_c6_poe_eth: "Sentinel board",
  waveshare_esp32_p4_eth: "Sentinel board",
  "waveshare-esp32-p4-c6-poe-eth": "Sentinel board",
  "waveshare-esp32-p4-eth": "Sentinel board",
});

export function boardKindSay(raw) {
  const s = String(raw || "").trim();
  if (!s || s === "—") return "—";
  const key = s.toLowerCase().replace(/\s+/g, "");
  if (BOARD_KIND_SAY[key]) return BOARD_KIND_SAY[key];
  if (looksLikeCode(s)) return "This board";
  return s.slice(0, 64);
}

function cameraVendorSay(blob) {
  const t = String(blob || "").toLowerCase();
  if (/\bdahua\b/.test(t)) return "Dahua";
  if (/\bhikvision\b/.test(t)) return "Hikvision";
  if (/\bonvif\b/.test(t)) return "Onvif camera";
  return "";
}

export function cameraLabelSay(raw) {
  const s = String(raw || "").trim();
  if (!s) return "This camera";
  const vendor = cameraVendorSay(s);
  if (vendor) return vendor;
  if (looksLikeCode(s)) return "This camera";
  return s.slice(0, 64);
}

/** On-site camera plant chip. Never paint the host board id. Two cameras on one board stay two chips. */
export function cameraPlantSay(row, index) {
  const r = row && typeof row === "object" ? row : {};
  const human = String(r.camera_label || r.label || r.camera_name || "").trim();
  if (human && !looksLikeCode(human) && !/^(rtsp|http|https)$/i.test(human)) {
    const named = cameraVendorSay(human);
    if (named) return named;
    return operatorSay(human, human.slice(0, 64));
  }
  const vendor = cameraVendorSay([
    human, r.camera_id, r.source_id, r.brand, r.protocol, r.vendor, r.camera_label,
  ].join(" "));
  if (vendor) return vendor;
  const cam = String(r.camera_id || "").trim();
  if (cam && !looksLikeCode(cam) && !/^(rtsp|http|https)$/i.test(cam)) {
    return operatorSay(cam, "This camera");
  }
  const n = Number(index);
  if (Number.isFinite(n) && n >= 0) return "Camera " + (Math.floor(n) + 1);
  return "This camera";
}

export function caseHeadingOf(row, queue, fallback) {
  const r = row && typeof row === "object" ? row : {};
  const q = queue && typeof queue === "object" ? queue : {};
  const cands = [r.title, r.summary, r.category, r.zone, q.zone, q.who, q.label];
  for (let i = 0; i < cands.length; i++) {
    const s = String(cands[i] || "").trim();
    if (s && !looksLikeCode(s)) return operatorSay(s, s);
  }
  return fallback || "This case";
}

export function capsSay(list) {
  const arr = Array.isArray(list) ? list : [];
  if (!arr.length) return "—";
  return arr.map((c) => operatorSay(c, "A planned step")).join(" · ");
}
